import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'

export const POLL_INTERVAL_MS = 2500
export const SCAN_HARD_CAP_MS = 300_000

interface UseScanPollingOptions {
  active: boolean
  startedAtMs: number
  reloadKeys: string[]
  intervalMs?: number
  hardCapMs?: number
}

interface UseScanPollingResult {
  timedOut: boolean
}

// Orchestrates Inertia partial-reload polling while a scan is active, giving up at a hard cap.
// It only drives polling and the timeout signal — every scan-state decision stays server-truthful
// and is made by the calling page.
export function useScanPolling({
  active,
  startedAtMs,
  reloadKeys,
  intervalMs = POLL_INTERVAL_MS,
  hardCapMs = SCAN_HARD_CAP_MS,
}: UseScanPollingOptions): UseScanPollingResult {
  const [timedOut, setTimedOut] = useState(() => Date.now() - startedAtMs >= hardCapMs)

  // Join to a stable primitive so a fresh array literal each render doesn't restart the interval.
  const reloadKey = reloadKeys.join(',')

  useEffect(() => {
    if (!active || timedOut) {return}

    if (Date.now() - startedAtMs >= hardCapMs) {
      setTimedOut(true)
      return
    }

    const interval = setInterval(() => {
      if (Date.now() - startedAtMs >= hardCapMs) {
        setTimedOut(true)
        return
      }
      router.reload({ only: reloadKey.split(',') })
    }, intervalMs)

    return () => clearInterval(interval)
  }, [active, timedOut, startedAtMs, hardCapMs, intervalMs, reloadKey])

  return { timedOut }
}
